// firebase/leads.service.js
import {
    collection,
    addDoc,
    getDocs,
    getDoc,
    query,
    orderBy,
    serverTimestamp,
    where,
    doc,
    updateDoc,
    onSnapshot,
    deleteField,
} from "firebase/firestore";
import { db } from "./firebase.config";
import { syncConversationContactInfo } from "./conversation.service";

// ============================================================
// LEAD TYPE DEFINITION (JSDoc)
// ============================================================

/**
 * @typedef {Object} InterestedProperty
 * @property {string} id - Property document ID
 * @property {string} address - Display address
 */

/**
 * @typedef {Object} Lead
 * @property {string} id - Lead document ID
 * @property {string} userId - Owner's user ID
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} fullName
 * @property {string} name - Alias for fullName (backward compat)
 * @property {string} email
 * @property {string} phone
 * @property {string} address
 * @property {InterestedProperty[]} interestedProperties - SINGLE SOURCE OF TRUTH for property interests
 * @property {string|null} portfolioId
 * @property {string|null} formResponseId
 * @property {'Active'|'Closed'|'Archived'|'Cold'} status
 * @property {string[]} tags
 * @property {string} notes
 * @property {string|null} conversation_id
 * @property {import('firebase/firestore').Timestamp} createdAt
 * @property {import('firebase/firestore').Timestamp} updatedAt
 *
 * DEPRECATED FIELDS (must not exist on new leads, auto-migrated on old):
 * - interestedProperty (string) — REMOVED
 * - propertyId (string) — REMOVED
 */

// ============================================================
// LEGACY FIELD RUNTIME SAFEGUARD
// ============================================================
const LEGACY_PROPERTY_FIELDS = ['interestedProperty', 'propertyId'];

/**
 * Runtime guard: detect legacy property fields on a lead object.
 * Logs a warning and triggers auto-migration when found.
 * Call this after any read/write to ensure no legacy data persists.
 *
 * @param {Object} lead - Lead document (must include `id`)
 * @param {string} [context] - Descriptive label for the log message
 */
const warnAndMigrateLegacyFields = (lead, context = 'unknown') => {
    if (!lead || !lead.id) return;
    const found = LEGACY_PROPERTY_FIELDS.filter(f => f in lead && lead[f] !== undefined);
    if (found.length === 0) return;

    console.warn(
        `[leads.service][LEGACY] Detected deprecated field(s) [${found.join(', ')}] on lead ${lead.id} (context: ${context}). Auto-migrating.`
    );
    // Fire-and-forget migration
    migrateLegacyPropertyFields(lead.id, lead).catch(err =>
        console.error('[leads.service][LEGACY] Auto-migration failed:', lead.id, err)
    );
};

/**
 * Lead status values
 * ONLY these four statuses are valid - no pipeline stages
 */
export const LEAD_STATUS = {
    ACTIVE: 'Active',
    CLOSED: 'Closed',
    ARCHIVED: 'Archived',
    COLD: 'Cold',
};

/**
 * Valid status values array for validation
 */
export const VALID_STATUSES = Object.values(LEAD_STATUS);

/**
 * Generate a fullName from firstName and lastName components
 * Used for consistent name handling across the application
 * @param {string} firstName - First name
 * @param {string} lastName - Last name
 * @returns {string} - Combined full name, trimmed
 */
export const generateFullName = (firstName, lastName) => {
    const first = (firstName || '').trim();
    const last = (lastName || '').trim();
    return `${first} ${last}`.trim();
};

/**
 * Normalize tags for storage and display
 * Ensures array format, removes empty/duplicate values, trims whitespace
 * @param {string[]|string} tags - Tags as array or comma-separated string
 * @returns {string[]} - Normalized array of non-empty tags
 */
const normalizeTags = (tags) => {
    if (!tags) return [];

    // If it's a string (from form input), split by comma
    const tagArray = typeof tags === 'string'
        ? tags.split(',').map(t => t.trim())
        : Array.isArray(tags) ? tags : [];

    // Remove empty values and duplicates
    const unique = new Set(tagArray.filter(t => t.length > 0));
    return Array.from(unique);
};

/**
 * Merge new tags into existing tags (case-insensitive deduplication)
 * @param {string[]} existingTags - Current tags on the lead
 * @param {string[]} newTags - New tags to add
 * @returns {string[]} - Merged array of unique tags
 */
const mergeTags = (existingTags = [], newTags = []) => {
    const normalizedExisting = normalizeTags(existingTags);
    const normalizedNew = normalizeTags(newTags);

    // Use lowercase keys for deduplication, preserve original casing (first seen wins)
    const tagMap = new Map();
    [...normalizedExisting, ...normalizedNew].forEach(tag => {
        const lowerKey = tag.toLowerCase();
        if (!tagMap.has(lowerKey)) {
            tagMap.set(lowerKey, tag);
        }
    });

    return Array.from(tagMap.values());
};

/**
 * Get all leads for a specific user
 * @param {string} uid - Owner's user ID
 * @returns {Promise<Array>} - Array of lead documents
 */
export const getUserLeads = async (uid) => {
    if (!uid) throw new Error('userId is required');

    const leadsRef = collection(db, 'leads');
    const q = query(leadsRef, where('userId', '==', uid), orderBy('createdAt', 'desc'));

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
    }));
};

/**
 * Subscribe to real-time updates of user's leads
 * @param {string} uid - Owner's user ID
 * @param {Function} callback - Called with updated leads array
 * @returns {Function} - Unsubscribe function
 */
export const onLeadsChange = (uid, callback) => {
    if (!uid) {
        callback([]);
        return () => { };
    }

    const leadsRef = collection(db, 'leads');
    const q = query(leadsRef, where('userId', '==', uid), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
        const leads = snapshot.docs.map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data(),
        }));

        // Runtime safeguard: detect & auto-migrate any lead with legacy fields
        leads.forEach(lead => warnAndMigrateLegacyFields(lead, 'onLeadsChange'));

        callback(leads);
    }, (error) => {
        console.error('[leads.service] Error in leads listener:', error);
        callback([]);
    });

    return unsubscribe;
};

/**
 * Add a new lead for a user
 * @param {string} uid - Owner's user ID
 * @param {Object} leadData - Lead data including name, firstName, lastName, email, phone, tags, etc.
 * @returns {Promise<string>} - New lead document ID
 */
export const addLeadForUser = async (uid, leadData) => {
    if (!uid) throw new Error('userId is required');

    // Normalize email for consistent de-duplication
    const normalizedEmail = leadData.email ? leadData.email.toLowerCase().trim() : '';

    // Generate fullName from firstName/lastName if provided, otherwise use name
    const fullName = leadData.firstName || leadData.lastName
        ? generateFullName(leadData.firstName, leadData.lastName)
        : (leadData.name || '');

    const leadsRef = collection(db, 'leads');

    const docRef = await addDoc(leadsRef, {
        // Required fields
        userId: uid,
        name: fullName, // Keep 'name' for backward compatibility
        fullName: fullName, // New standardized field
        firstName: (leadData.firstName || '').trim(),
        lastName: (leadData.lastName || '').trim(),
        email: normalizedEmail,
        phone: leadData.phone || '',

        // Optional fields
        address: leadData.address || '',
        tags: normalizeTags(leadData.tags),

        // Interested properties (single source of truth — array of {id, address})
        interestedProperties: leadData.interestedProperties || [],

        // Portfolio reference
        portfolioId: leadData.portfolioId || null,
        formResponseId: leadData.formResponseId || null,

        // Status & notes
        status: leadData.status || LEAD_STATUS.ACTIVE,
        notes: leadData.notes || '',

        // Conversation link (for inbox integration)
        conversation_id: leadData.conversation_id || null,

        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });

    console.log('[leads.service] Lead created:', docRef.id, 'for user:', uid);
    return docRef.id;
};

/**
 * Update lead fields
 * @param {string} leadId - Lead document ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateLead = async (leadId, updates) => {
    if (!leadId) throw new Error('leadId is required');

    const leadRef = doc(db, 'leads', leadId);
    await updateDoc(leadRef, {
        ...updates,
        updatedAt: serverTimestamp(),
    });
};

/**
 * Update lead tags
 * Useful for editing tags in the Lead Drawer
 * @param {string} leadId - Lead document ID
 * @param {string[]|string} tags - Tags as array or comma-separated string
 * @returns {Promise<void>}
 */
export const updateLeadTags = async (leadId, tags) => {
    if (!leadId) throw new Error('leadId is required');

    const normalizedTags = normalizeTags(tags);
    await updateLead(leadId, { tags: normalizedTags });
    console.log('[leads.service] Lead tags updated:', leadId, 'tags:', normalizedTags);
};

/**
 * Get a single lead by ID
 * @param {string} leadId - Lead document ID
 * @returns {Promise<Object|null>}
 */
export const getLeadById = async (leadId) => {
    if (!leadId) return null;

    const leadRef = doc(db, 'leads', leadId);
    const docSnap = await getDoc(leadRef);

    if (!docSnap.exists()) return null;

    const lead = {
        id: docSnap.id,
        ...docSnap.data(),
    };

    // Runtime safeguard
    warnAndMigrateLegacyFields(lead, 'getLeadById');

    return lead;
};

/**
 * Update lead status
 * Only allows valid statuses: Active, Closed, Archived, Cold
 * @param {string} leadId - Lead document ID
 * @param {string} status - New status value
 * @returns {Promise<void>}
 */
export const updateLeadStatus = async (leadId, status) => {
    if (!leadId) throw new Error('leadId is required');
    if (!VALID_STATUSES.includes(status)) {
        throw new Error(`Invalid status: ${status}. Must be one of: ${VALID_STATUSES.join(', ')}`);
    }

    // Keep archive state + tags synchronized with status changes.
    if (status === LEAD_STATUS.ARCHIVED) {
        await updateLeadArchiveState(leadId, true);
    } else {
        await updateLeadArchiveState(leadId, false, status);
    }
    console.log('[leads.service] Lead status updated:', leadId, 'status:', status);
};

/**
 * Update lead archive state and keep status/tags synchronized.
 * - Archiving sets archived=true, status='Archived', and ensures 'archived' tag exists
 * - Unarchiving sets archived=false, removes 'archived' tag, and sets status to provided value (default 'Active')
 * @param {string} leadId - Lead document ID
 * @param {boolean} isArchived - Archive state to set
 * @param {string} [unarchivedStatus='Active'] - Status to apply when unarchiving
 * @returns {Promise<void>}
 */
export const updateLeadArchiveState = async (leadId, isArchived, unarchivedStatus = LEAD_STATUS.ACTIVE) => {
    if (!leadId) throw new Error('leadId is required');
    if (!isArchived && !VALID_STATUSES.includes(unarchivedStatus)) {
        throw new Error(`Invalid status: ${unarchivedStatus}. Must be one of: ${VALID_STATUSES.join(', ')}`);
    }

    const leadRef = doc(db, 'leads', leadId);
    const leadSnapshot = await getDoc(leadRef);
    if (!leadSnapshot.exists()) {
        throw new Error(`Lead not found: ${leadId}`);
    }

    const leadData = leadSnapshot.data() || {};
    const existingTags = normalizeTags(leadData.tags || []);
    const tagsWithoutArchived = existingTags.filter(tag => tag.toLowerCase() !== 'archived');
    const nextTags = isArchived ? [...tagsWithoutArchived, 'archived'] : tagsWithoutArchived;

    await updateLead(leadId, {
        archived: isArchived,
        status: isArchived ? LEAD_STATUS.ARCHIVED : unarchivedStatus,
        tags: nextTags,
    });
};

/**
 * Archive a lead (soft delete)
 * Sets archived: true and status to 'Archived'
 * @param {string} leadId - Lead document ID
 * @returns {Promise<void>}
 */
export const archiveLead = async (leadId) => {
    if (!leadId) throw new Error('leadId is required');

    await updateLeadArchiveState(leadId, true);
    console.log('[leads.service] Lead archived:', leadId);
};

/**
 * Unarchive a lead
 * Sets archived: false and restores status to 'Active'
 * @param {string} leadId - Lead document ID
 * @returns {Promise<void>}
 */
export const unarchiveLead = async (leadId) => {
    if (!leadId) throw new Error('leadId is required');

    await updateLeadArchiveState(leadId, false, LEAD_STATUS.ACTIVE);
    console.log('[leads.service] Lead unarchived:', leadId);
};

/**
 * Delete all messages in a conversation subcollection
 * @param {string} conversationId - Conversation document ID
 * @returns {Promise<number>} - Number of messages deleted
 */
const deleteConversationMessages = async (conversationId) => {
    if (!conversationId) return 0;

    try {
        const messagesRef = collection(db, `conversations/${conversationId}/messages`);
        const messagesSnapshot = await getDocs(messagesRef);

        if (messagesSnapshot.empty) {
            return 0;
        }

        const { deleteDoc: deleteDocFn } = await import('firebase/firestore');
        const deletePromises = messagesSnapshot.docs.map(msgDoc =>
            deleteDocFn(doc(db, `conversations/${conversationId}/messages`, msgDoc.id))
        );

        await Promise.all(deletePromises);
        console.log(`[leads.service] Deleted ${messagesSnapshot.size} messages from conversation:`, conversationId);
        return messagesSnapshot.size;
    } catch (error) {
        console.error('[leads.service] Error deleting conversation messages:', error);
        throw error;
    }
};

/**
 * Delete a conversation and all its messages
 * @param {string} conversationId - Conversation document ID
 * @returns {Promise<void>}
 */
const deleteConversation = async (conversationId) => {
    if (!conversationId) return;

    try {
        // First delete all messages in the subcollection
        await deleteConversationMessages(conversationId);

        // Then delete the conversation document itself
        const { deleteDoc: deleteDocFn } = await import('firebase/firestore');
        const conversationRef = doc(db, 'conversations', conversationId);
        await deleteDocFn(conversationRef);
        console.log('[leads.service] Deleted conversation:', conversationId);
    } catch (error) {
        console.error('[leads.service] Error deleting conversation:', error);
        throw error;
    }
};

/**
 * Find all conversations associated with a lead
 * @param {string} leadId - Lead document ID
 * @param {string} userId - User ID (required for Firestore security rules)
 * @returns {Promise<Array>} - Array of conversation documents
 */
const findConversationsByLeadId = async (leadId, userId) => {
    if (!leadId || !userId) return [];

    try {
        const conversationsRef = collection(db, 'conversations');
        // Must include userId in query to satisfy Firestore security rules
        const q = query(
            conversationsRef,
            where('userId', '==', userId),
            where('leadId', '==', leadId)
        );
        const snapshot = await getDocs(q);

        return snapshot.docs.map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data(),
        }));
    } catch (error) {
        console.error('[leads.service] Error finding conversations for lead:', error);
        return [];
    }
};

/**
 * Delete a lead permanently along with all associated data
 * This includes:
 * - All conversations linked to the lead (via leadId field)
 * - All messages within those conversations
 * - The lead document itself
 * 
 * Note: FormResponses are NOT deleted as they are historical records
 * 
 * @param {string} leadId - Lead document ID
 * @returns {Promise<{ success: boolean, deletedConversations: number, deletedMessages: number }>}
 */
export const deleteLead = async (leadId) => {
    if (!leadId) throw new Error('leadId is required');

    let deletedConversations = 0;
    let deletedMessages = 0;

    try {
        // Step 1: Get the lead to retrieve userId (needed for security rules)
        const lead = await getLeadById(leadId);
        if (!lead) {
            throw new Error('Lead not found');
        }
        const userId = lead.userId;

        // Step 2: Find all conversations associated with this lead
        const conversations = await findConversationsByLeadId(leadId, userId);
        console.log(`[leads.service] Found ${conversations.length} conversations for lead:`, leadId);

        // Step 3: Delete each conversation and its messages
        for (const conversation of conversations) {
            try {
                const msgCount = await deleteConversationMessages(conversation.id);
                deletedMessages += msgCount;

                const { deleteDoc: deleteDocFn } = await import('firebase/firestore');
                const conversationRef = doc(db, 'conversations', conversation.id);
                await deleteDocFn(conversationRef);
                deletedConversations++;

                console.log('[leads.service] Deleted conversation:', conversation.id);
            } catch (convError) {
                console.error('[leads.service] Error deleting conversation:', conversation.id, convError);
                // Continue with other conversations even if one fails
            }
        }

        // Step 4: Delete the lead document itself
        const { deleteDoc: deleteDocFn } = await import('firebase/firestore');
        const leadRef = doc(db, 'leads', leadId);
        await deleteDocFn(leadRef);

        console.log('[leads.service] Lead deleted:', leadId, {
            deletedConversations,
            deletedMessages,
        });

        return { success: true, deletedConversations, deletedMessages };
    } catch (error) {
        console.error('[leads.service] Error deleting lead:', leadId, error);
        throw error;
    }
};

/**
 * Update lead contact details
 * @param {string} leadId - Lead document ID
 * @param {Object} details - Contact details to update
 * @param {string} [details.name] - Lead name (deprecated, use firstName/lastName)
 * @param {string} [details.firstName] - Lead first name
 * @param {string} [details.lastName] - Lead last name
 * @param {string} [details.email] - Lead email
 * @param {string} [details.phone] - Lead phone
 * @param {string} [details.address] - Lead address
 * @param {string} [details.notes] - Lead notes
 * @param {Array} [details.interestedProperties] - Array of interested properties [{id, address}]
 * @returns {Promise<void>}
 */
export const updateLeadDetails = async (leadId, details) => {
    if (!leadId) throw new Error('leadId is required');

    // Only update provided fields
    const updateFields = {};

    // Handle firstName/lastName updates - recalculate fullName
    if (details.firstName !== undefined || details.lastName !== undefined) {
        const firstName = (details.firstName !== undefined ? details.firstName : '').trim();
        const lastName = (details.lastName !== undefined ? details.lastName : '').trim();
        const fullName = generateFullName(firstName, lastName);

        updateFields.firstName = firstName;
        updateFields.lastName = lastName;
        updateFields.fullName = fullName;
        updateFields.name = fullName; // Keep name in sync for backward compatibility
    } else if (details.name !== undefined) {
        // Legacy support: if only name is provided, update name and fullName
        updateFields.name = details.name;
        updateFields.fullName = details.name;
    }

    if (details.email !== undefined) updateFields.email = details.email?.toLowerCase().trim() || '';
    if (details.phone !== undefined) updateFields.phone = details.phone;
    if (details.address !== undefined) updateFields.address = details.address;
    if (details.notes !== undefined) updateFields.notes = details.notes;

    // Support multiple interested properties (single source of truth)
    if (details.interestedProperties !== undefined) {
        updateFields.interestedProperties = details.interestedProperties || [];
        // Clean up legacy fields to prevent stale fallback data
        updateFields.interestedProperty = deleteField();
        updateFields.propertyId = deleteField();
    }

    if (Object.keys(updateFields).length > 0) {
        await updateLead(leadId, updateFields);
        console.log('[leads.service] Lead details updated:', leadId, updateFields);

        // Sync contact-related changes to the linked conversation so
        // that outbound messages always use the latest email / phone.
        const contactFields = ['email', 'phone', 'firstName', 'lastName', 'fullName', 'name'];
        const convoSync = {};
        contactFields.forEach((f) => {
            if (updateFields[f] !== undefined) convoSync[f] = updateFields[f];
        });
        if (Object.keys(convoSync).length > 0) {
            // Fire-and-forget: don't block on conversation sync
            syncConversationContactInfo(leadId, convoSync).catch((err) =>
                console.error('[leads.service] Failed to sync conversation contact info:', err)
            );
        }
    }
};

// ============================================================
// LEGACY FIELD MIGRATION
// ============================================================

/**
 * Migrate a lead from legacy single-property fields to the standardized schema.
 * If a lead has `interestedProperty` (string) or `propertyId` (string) but no
 * `interestedProperties` array, this converts the legacy fields into the array
 * format and deletes the old fields.
 *
 * Safe to call on already-migrated leads (no-op if interestedProperties exists).
 *
 * @param {string} leadId - Lead document ID
 * @param {Object} leadData - Current lead document data
 * @returns {Promise<boolean>} - true if migration was performed
 */
export const migrateLegacyPropertyFields = async (leadId, leadData) => {
    if (!leadId || !leadData) return false;

    const hasArray = Array.isArray(leadData.interestedProperties) && leadData.interestedProperties.length > 0;
    const hasLegacyString = !!leadData.interestedProperty;
    const hasLegacyId = !!leadData.propertyId;

    // Nothing to migrate if already using array or no legacy fields exist
    if (hasArray || (!hasLegacyString && !hasLegacyId)) return false;

    // Build interestedProperties from legacy propertyId
    const interestedProperties = [];
    if (hasLegacyId) {
        try {
            const propRef = doc(db, 'properties', leadData.propertyId);
            const propSnap = await getDoc(propRef);
            if (propSnap.exists()) {
                const propData = propSnap.data();
                interestedProperties.push({
                    id: leadData.propertyId,
                    address: propData.address || propData.name || leadData.interestedProperty || leadData.propertyId,
                });
            } else {
                // Property doc doesn't exist — use display string if available
                interestedProperties.push({
                    id: leadData.propertyId,
                    address: leadData.interestedProperty || leadData.propertyId,
                });
            }
        } catch (err) {
            console.warn('[leads.service] Migration: failed to fetch property', leadData.propertyId, err);
            interestedProperties.push({
                id: leadData.propertyId,
                address: leadData.interestedProperty || leadData.propertyId,
            });
        }
    }

    // Update lead: write array, delete legacy fields
    const leadRef = doc(db, 'leads', leadId);
    await updateDoc(leadRef, {
        interestedProperties,
        interestedProperty: deleteField(),
        propertyId: deleteField(),
        updatedAt: serverTimestamp(),
    });

    console.log('[leads.service] Migrated legacy property fields for lead:', leadId);
    return true;
};

// ============================================================
// LEAD DE-DUPLICATION (for form submissions)
// ============================================================

/**
 * Find an existing lead by owner ID and email
 * Used for de-duplication: one owner can have only one lead per email
 * 
 * @param {string} userId - Owner's user ID
 * @param {string} email - Lead's email address
 * @returns {Promise<Object|null>} - Existing lead document or null
 */
export const findLeadByEmail = async (userId, email) => {
    if (!userId || !email) return null;

    // Normalize email for comparison
    const normalizedEmail = email.toLowerCase().trim();
    if (!normalizedEmail) return null;

    try {
        const leadsRef = collection(db, 'leads');
        const q = query(
            leadsRef,
            where('userId', '==', userId),
            where('email', '==', normalizedEmail)
        );

        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            return null;
        }

        // Return the first match (should be unique per owner+email)
        const docSnap = snapshot.docs[0];
        return {
            id: docSnap.id,
            ...docSnap.data(),
        };
    } catch (error) {
        console.error('[leads.service] Error finding lead by email:', error);
        return null;
    }
};

/**
 * Upsert a lead from form submission (de-duplicated)
 * 
 * De-duplication rule: One lead per (userId + email)
 * - If lead exists → UPDATE with new form data (preserve status/notes)
 * - If lead doesn't exist → CREATE new lead
 * 
 * @param {string} userId - Owner's user ID (REQUIRED)
 * @param {Object} formData - Form submission data
 * @param {string} formData.name - Lead name (combined, or use firstName/lastName)
 * @param {string} formData.firstName - Lead first name
 * @param {string} formData.lastName - Lead last name
 * @param {string} formData.email - Lead email (used for de-duplication)
 * @param {string} formData.phone - Lead phone
 * @param {string} formData.address - Lead address
 * @param {Array} formData.interestedProperties - Array of interested properties [{id, address}]
 * @param {string} formData.portfolioId - Portfolio ID
 * @param {string} formData.formResponseId - Form response document ID
 * @returns {Promise<{ leadId: string, isNew: boolean }>}
 */
export const upsertLeadFromForm = async (userId, formData) => {
    if (!userId) throw new Error('userId is required');
    if (!formData.email) throw new Error('email is required for lead upsert');

    // Normalize email for consistent de-duplication
    const normalizedEmail = formData.email.toLowerCase().trim();

    // Generate fullName consistently
    const fullName = formData.firstName || formData.lastName
        ? generateFullName(formData.firstName, formData.lastName)
        : (formData.name || '');

    // Parse fullName into firstName / lastName (used for both CREATE and UPDATE)
    const parsedFirstName = formData.firstName
        ? formData.firstName.trim()
        : (fullName.split(' ')[0] || '');
    const parsedLastName = formData.lastName
        ? formData.lastName.trim()
        : (fullName.split(' ').slice(1).join(' ') || '');

    // Step 1: Check for existing lead with same owner + email
    const existingLead = await findLeadByEmail(userId, normalizedEmail);

    if (existingLead) {
        // Step 2a: UPDATE existing lead
        // POLICY: Preserve existing personal data (name, phone) — only fill in blanks.
        // Tags are always MERGED. Status, notes, conversation_id, createdAt are never touched.
        console.log('[leads.service] Found existing lead:', existingLead.id, '- updating (fill blanks only)');

        // Merge new tags with existing tags (case-insensitive deduplication)
        const mergedTags = mergeTags(existingLead.tags, formData.tags);

        // Only fill in fields that are currently empty on the existing lead
        const existingFirstName = (existingLead.firstName || '').trim();
        const existingLastName = (existingLead.lastName || '').trim();
        const existingFullName = (existingLead.fullName || existingLead.name || '').trim();
        const existingPhone = (existingLead.phone || '').trim();
        const existingAddress = (existingLead.address || '').trim();

        const updateData = {
            // Personal data: only fill blanks, never overwrite existing values
            firstName: existingFirstName || parsedFirstName,
            lastName: existingLastName || parsedLastName,
            name: existingFullName || fullName,
            fullName: existingFullName || fullName,
            phone: existingPhone || (formData.phone || ''),
            address: existingAddress || (formData.address || ''),
            // Always update tracking fields
            lastFormResponseId: formData.formResponseId,
            tags: mergedTags,

            // Portfolio reference (update if provided)
            ...(formData.portfolioId && { portfolioId: formData.portfolioId }),

            // Clean up legacy fields
            interestedProperty: deleteField(),
            propertyId: deleteField(),

            // DO NOT overwrite: status, notes, conversation_id, createdAt
        };

        // Merge interestedProperties arrays (deduplicate by id)
        if (Array.isArray(formData.interestedProperties) && formData.interestedProperties.length > 0) {
            const existingProps = existingLead.interestedProperties || [];
            const existingIds = new Set(existingProps.map(p => p.id));
            const merged = [...existingProps];
            for (const prop of formData.interestedProperties) {
                if (!existingIds.has(prop.id)) {
                    merged.push(prop);
                }
            }
            updateData.interestedProperties = merged;
        }

        await updateLead(existingLead.id, updateData);

        console.log('[leads.service] Lead updated:', existingLead.id, 'for user:', userId);
        return { leadId: existingLead.id, isNew: false };
    } else {
        // Step 2b: CREATE new lead
        console.log('[leads.service] No existing lead found for email:', normalizedEmail, '- creating new lead');

        const leadId = await addLeadForUser(userId, {
            firstName: parsedFirstName,
            lastName: parsedLastName,
            name: fullName,
            email: normalizedEmail,
            phone: formData.phone || '',
            address: formData.address || '',
            interestedProperties: formData.interestedProperties || [],
            portfolioId: formData.portfolioId || null,
            formResponseId: formData.formResponseId || null,
            tags: formData.tags || [],
            status: LEAD_STATUS.ACTIVE,
            notes: '',
        });

        return { leadId, isNew: true };
    }
};
